import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import PropTypes from "prop-types";

import usePrevious from "src/util/usePrevious";
import { useAuth0 } from "src/contexts/auth0Provider";
import { useAISidebar } from "src/hooks/useAISidebar";
import { EventBus, EVENTS } from "src/services/eventEmitter";
import {
  useApolloSettings,
  useTenantId,
  useDateSettings,
} from "src/contexts/global/ApolloSettingsContext";
import {
  useWorkspace,
  useWorkspaceFeatureFlags,
} from "src/contexts/global/WorkspaceContext";
import { API_DATE_FORMAT } from "src/consts/global";
import { hasTenantPrompt } from "src/services/auth";

import { AGENTCORE_NAMES, ASK_TEAMFORM_MODES } from "../consts";
import useGetAIChat from "../context/hooks/useGetAIChat";
import { getRetryTargetMessage } from "./utils/getRetryTargetMessage";
import { useAIChatPhase, CHAT_PHASE } from "./hooks/useAIChatPhase";
import { useAIChatStream } from "./hooks/useAIChatStream";
import { AIChatV3Context } from "./AIChatV3Context";

const DASHBOARD_TAG_RE = /<dashboard[^>]*\bid="([^"]+)"/;
const DASHBOARD_TITLE_RE = /<dashboard[^>]*\btitle="([^"]+)"/;

export const AIChatV3Provider = ({ children }) => {
  const authState = useAuth0();
  const isSupportUser = hasTenantPrompt(authState?.user);
  const workspace = useWorkspace();
  const { id: workspaceId } = workspace;
  const { enableSupportQuestionInAskTeamform } = useWorkspaceFeatureFlags();
  const [askTeamformMode, setAskTeamformMode] = useState(
    ASK_TEAMFORM_MODES.DATA
  );

  // Hoisted above the route outlet — always targets ask_teamform_v2; the
  // switchboard handles intent routing (answer | dashboard | refuse). Support
  // users can switch to the GQL expert for debugging.
  const [selectedAgent, setSelectedAgent] = useState(
    AGENTCORE_NAMES.ASK_TEAMFORM_V2
  );
  const [question, setQuestion] = useState("");
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [userHasScrolledUp, setUserHasScrolledUp] = useState(false);

  // Portal mount nodes for page-scoped pane extras (e.g. the dashboard debug
  // tabs). The pane (AIChatV3Interface) publishes these DOM nodes; a page can
  // read them and render into the pane via createPortal while keeping its own
  // React-tree context.
  const [tabBarNode, setTabBarNode] = useState(null);
  const [panelNode, setPanelNode] = useState(null);

  const { apiUrl } = useApolloSettings();
  const tenantId = useTenantId();
  const dateSettings = useDateSettings();
  const pitDate = dateSettings.toDate?.format(API_DATE_FORMAT);

  // -- State machine --
  const {
    chatPhase,
    error,
    transition,
    reset: resetPhase,
    setError,
    derived: { isComplete, isSubmitting },
  } = useAIChatPhase();

  // -- Chat CRUD --
  const {
    chatId,
    chats,
    loading: chatsLoading,
    onStartNewChat: rawOnStartNewChat,
    onSelectChat: rawOnSelectChat,
    adoptChatId,
    currentChat,
    updateChatOptimistically,
    onSubmitMessageFeedback,
    refetchChats,
  } = useGetAIChat();

  const rawMessages = currentChat?.messages;
  const messages = useMemo(() => rawMessages || [], [rawMessages]);
  const previousMessages = usePrevious(messages);

  // -- Dashboard detection on stream complete --
  const handleStreamComplete = useCallback((finalMessage) => {
    if (!finalMessage?.content?.includes("<dashboard")) {
      return;
    }

    const dashboardId = finalMessage.content.match(DASHBOARD_TAG_RE)?.[1];
    const title = finalMessage.content.match(DASHBOARD_TITLE_RE)?.[1];

    if (dashboardId || title) {
      EventBus.emit(EVENTS.AI_DASHBOARD_UPDATED, { dashboardId, title });
    }
  }, []);

  // -- Stream hook --
  const { send, cancel, resetPipeline, toolsUsedCount, pipelineSteps } =
    useAIChatStream({
      transition,
      tenantId,
      apiUrl,
      workspaceId,
      pitDate,
      getToken: authState.getTokenSilently,
      adoptChatId,
      updateChatOptimistically,
      refetchChats,
      selectedAgent,
      askTeamformMode,
      enableSupportQuestionInAskTeamform,
      onStreamComplete: handleStreamComplete,
    });

  // Dashboard consumers (docked pane, canvas, router guard) track a single
  // `isStreaming` boolean. Derive it from `isSubmitting` so the ERROR phase
  // (a finished-but-failed build) is not treated as still streaming.
  const isStreaming = isSubmitting;

  // -- Session clear --
  const onSessionClear = useCallback(() => {
    setUserHasScrolledUp(false);
    setQuestion("");
    setEditingMessageId(null);
    setAskTeamformMode(ASK_TEAMFORM_MODES.DATA);
    resetPhase();
    resetPipeline();
  }, [resetPhase, resetPipeline]);

  // Switching to / starting another thread must drop the prior build's
  // pipeline checklist; chatId-based reset would also fire mid-build when
  // adoptChatId swaps the temp id, so wrap the explicit thread actions.
  const onStartNewChat = useCallback(
    (...args) => {
      // History/thread switches must not keep Help mode from a prior chat.
      setAskTeamformMode(ASK_TEAMFORM_MODES.DATA);
      resetPipeline();
      return rawOnStartNewChat(...args);
    },
    [rawOnStartNewChat, resetPipeline]
  );

  const onSelectChat = useCallback(
    (...args) => {
      setAskTeamformMode(ASK_TEAMFORM_MODES.DATA);
      resetPipeline();
      return rawOnSelectChat(...args);
    },
    [rawOnSelectChat, resetPipeline]
  );

  // -- Version ID lifecycle --
  const { versionId } = useAISidebar();
  const previousVersionId = useRef(versionId);

  useEffect(() => {
    if (previousVersionId.current && !versionId) {
      onSessionClear();
    }
    previousVersionId.current = versionId;
  }, [onSessionClear, versionId]);

  // -- Send question --
  const onSendQuestion = useCallback(() => {
    const prompt = question.trim();
    if (!prompt) {
      return;
    }

    const isEdit = !!editingMessageId;

    setQuestion("");
    setEditingMessageId(null);

    if (isEdit) {
      // Update the local message content optimistically
      const messageToUpdate = messages.find((m) => m.id === editingMessageId);
      if (messageToUpdate) {
        updateChatOptimistically({ ...messageToUpdate, content: prompt });
      }
    }

    // If the current chat is in an error state and this isn't an edit/retry,
    // start a fresh chat rather than appending to the broken one.
    if (!isEdit && chatPhase === CHAT_PHASE.ERROR) {
      onStartNewChat();
      send(prompt, { chatId: null, isRetry: false });
      return;
    }

    send(prompt, { chatId, isRetry: isEdit });
  }, [
    question,
    editingMessageId,
    messages,
    chatId,
    chatPhase,
    send,
    onStartNewChat,
    updateChatOptimistically,
  ]);

  // -- Retry --
  const lastUserMessage = useMemo(
    () => getRetryTargetMessage(messages),
    [messages]
  );

  const canRetryOrEdit = useMemo(
    () =>
      (chatPhase === CHAT_PHASE.ERROR && !!lastUserMessage) ||
      (isComplete && !!lastUserMessage),
    [chatPhase, isComplete, lastUserMessage]
  );

  const onRetry = useCallback(() => {
    if (!lastUserMessage?.content) {
      return;
    }
    send(lastUserMessage.content, { chatId, isRetry: true });
  }, [lastUserMessage, chatId, send]);

  // -- Edit --
  const onEditLastMessage = useCallback(() => {
    if (!lastUserMessage) {
      return;
    }
    setQuestion(lastUserMessage.content || "");
    setEditingMessageId(lastUserMessage.id);
    setError(null);
  }, [lastUserMessage, setError]);

  const onCancelEdit = useCallback(() => {
    setEditingMessageId(null);
    setQuestion("");
  }, []);

  // -- Context value --
  const value = useMemo(
    () => ({
      chatPhase,
      isComplete,
      isStreaming,
      pipelineSteps,

      tabBarNode,
      setTabBarNode,
      panelNode,
      setPanelNode,

      chatId,
      messages,
      previousMessages,

      toolsUsedCount,

      question,
      setQuestion,
      editingMessageId,

      onSendQuestion,
      onRetry,
      onCancel: cancel,
      onEditLastMessage,
      onCancelEdit,
      onSessionClear,
      canRetryOrEdit,

      onStartNewChat,
      onSelectChat,
      chats,
      chatsLoading,

      onSubmitMessageFeedback,

      selectedAgent: isSupportUser
        ? selectedAgent
        : AGENTCORE_NAMES.ASK_TEAMFORM_V2,
      setSelectedAgent: isSupportUser ? setSelectedAgent : () => {},
      isSupportUser,

      askTeamformMode,
      setAskTeamformMode,
      enableSupportQuestionInAskTeamform,

      error,
      setError,

      setUserHasScrolledUp,
      userHasScrolledUp,
    }),
    [
      chatPhase,
      isComplete,
      isStreaming,
      pipelineSteps,
      tabBarNode,
      panelNode,
      chatId,
      messages,
      previousMessages,
      toolsUsedCount,
      question,
      editingMessageId,
      onSendQuestion,
      onRetry,
      cancel,
      onEditLastMessage,
      onCancelEdit,
      onSessionClear,
      canRetryOrEdit,
      onStartNewChat,
      onSelectChat,
      chats,
      chatsLoading,
      onSubmitMessageFeedback,
      selectedAgent,
      setSelectedAgent,
      isSupportUser,
      askTeamformMode,
      enableSupportQuestionInAskTeamform,
      error,
      setError,
      userHasScrolledUp,
    ]
  );

  // Always render children: this provider is hoisted above the route outlet,
  // so gating its output on a flag would blank the whole app. Sidebar chat
  // visibility is controlled separately by AINav (enableAIChat).
  return (
    <AIChatV3Context.Provider value={value}>
      {children}
    </AIChatV3Context.Provider>
  );
};

AIChatV3Provider.propTypes = {
  children: PropTypes.node.isRequired,
};
